import React, { useState, useMemo, useCallback } from 'react';
import moment from 'moment';

import { usePermissions, PLANNING } from 'permissions';
import Message from 'components/Message';
import Spinner from 'components/FullScreenSpinner';

import {
  ONE_WEEK,
  VIEW_WEEKLY,
  VIEW_MONTHLY,
  getInitDate,
  getInitMonthDate,
  getDateKey,
  getColumnCount,
  buildCalendarDays,
  groupDaysByWeek,
  getCalendarTitle,
  getWeekdayHeaders,
  isSamePeriod,
} from './utils';
import { useScheduleLines } from './hooks';
import ViewDropdown from './ViewDropdown';
import {
  Container,
  Panel,
  Button,
  Icon,
  Title,
  Head,
  HeadLabel,
  HeadText,
  WeekSection,
  WeekHead,
  ContentWrap,
  Item,
  DisabledItem,
  SpinnerWrap,
} from './styles';

const Calendar = () => {
  const initWeekDate = useMemo(() => getInitDate(), []);
  const initMonthDate = useMemo(() => getInitMonthDate(), []);
  const can = usePermissions();
  const [viewMode, setViewMode] = useState(VIEW_WEEKLY);
  const [showWeekends, setShowWeekends] = useState(false);
  const [startDate, setStartDate] = useState(initWeekDate);
  const today = useMemo(() => getDateKey(new Date()), []);
  const columnCount = getColumnCount(showWeekends);
  const initDate = viewMode === VIEW_MONTHLY ? initMonthDate : initWeekDate;

  const calendarDays = useMemo(
    () => buildCalendarDays({ startDate, viewMode, showWeekends, today }),
    [showWeekends, startDate, today, viewMode]
  );
  const weekGroups = useMemo(() => groupDaysByWeek(calendarDays), [calendarDays]);
  const weekdayHeaders = useMemo(() => getWeekdayHeaders(showWeekends), [showWeekends]);
  const title = useMemo(() => getCalendarTitle(calendarDays, viewMode), [calendarDays, viewMode]);
  const { list, loading, error } = useScheduleLines(calendarDays, columnCount);

  const onReset = useCallback(() => setStartDate(initDate), [initDate]);
  const onPrev = useCallback(() => {
    setStartDate(($) => {
      if (viewMode === VIEW_MONTHLY) return moment($).subtract(1, 'month').startOf('month').toDate();

      return new Date($.valueOf() - ONE_WEEK);
    });
  }, [viewMode]);
  const onNext = useCallback(() => {
    setStartDate(($) => {
      if (viewMode === VIEW_MONTHLY) return moment($).add(1, 'month').startOf('month').toDate();

      return new Date($.valueOf() + ONE_WEEK);
    });
  }, [viewMode]);
  const onViewModeChange = useCallback((nextViewMode) => {
    setViewMode(nextViewMode);
    setStartDate(($) => {
      if (nextViewMode === VIEW_MONTHLY) return moment($).startOf('month').toDate();

      return moment($).startOf('isoWeek').toDate();
    });
  }, []);

  const renderItems = useCallback(
    (weekIndex = 0) =>
      list.flatMap((items) =>
        items
          .filter(({ weekIndex: itemWeekIndex = 0 }) => itemWeekIndex === weekIndex)
          .map(({ id, column, row, name, canton, days, color, status }) => {
            const disabled = !can.update(PLANNING) && status < 1;
            const Component = disabled ? DisabledItem : Item;

            return (
              <Component
                key={`${weekIndex}-${id}-${column}-${row}`}
                {...(disabled ? {} : { to: `/planning/${id}` })}
                $column={column}
                $row={row}
                $days={days}
                $columns={columnCount}
                $color={color}
              >
                {canton && <span>{canton} </span>}
                <span>{name}</span>
              </Component>
            );
          })
      ),
    [can, columnCount, list]
  );

  return (
    <Container>
      <Panel>
        <Button type="button" onClick={onReset} disabled={isSamePeriod(startDate, initDate, viewMode)}>
          Heute
        </Button>
        <Button type="button" onClick={onPrev}>
          <Icon $rotate />
        </Button>
        <Button type="button" onClick={onNext}>
          <Icon />
        </Button>
        <Title>{title}</Title>
        <ViewDropdown
          viewMode={viewMode}
          showWeekends={showWeekends}
          onViewModeChange={onViewModeChange}
          onShowWeekendsChange={setShowWeekends}
        />
      </Panel>
      {error && (
        <Message type="error" form={1}>
          {error}
        </Message>
      )}
      {viewMode === VIEW_MONTHLY ? (
        <>
          <Head $columns={columnCount}>
            {weekdayHeaders.map((weekday) => (
              <HeadLabel key={weekday}>{weekday}</HeadLabel>
            ))}
          </Head>
          {weekGroups.map((weekDays, weekIndex) => (
            <WeekSection key={weekDays.map(({ key }) => key).join('-')}>
              <WeekHead $columns={columnCount} $compact={weekIndex > 0}>
                {weekDays.map(({ key, day, isActive, isGray }) => (
                  <HeadText key={key} $isActive={isActive} $isGray={isGray}>
                    {day}
                  </HeadText>
                ))}
              </WeekHead>
              <ContentWrap $columns={columnCount}>
                {renderItems(weekIndex)}
                {loading && weekIndex === 0 && (
                  <SpinnerWrap>
                    <Spinner size={30} />
                  </SpinnerWrap>
                )}
              </ContentWrap>
            </WeekSection>
          ))}
        </>
      ) : (
        <>
          <Head $columns={columnCount}>
            {calendarDays.map(({ key, weekday, day, isActive, isGray }) => (
              <div key={key}>
                <HeadLabel $isActive={isActive} $isGray={isGray}>
                  {weekday}
                </HeadLabel>
                <HeadText $isActive={isActive} $isGray={isGray}>
                  {day}
                </HeadText>
              </div>
            ))}
          </Head>
          <ContentWrap $columns={columnCount}>
            {renderItems()}
            {loading && (
              <SpinnerWrap>
                <Spinner size={30} />
              </SpinnerWrap>
            )}
          </ContentWrap>
        </>
      )}
    </Container>
  );
};

export default Calendar;
